/**
 *
 * @module bamHlsPlayerAdapter
 * @desc PlayerAdapter for bam-hls devices like smart tv's
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/media.md
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/stream-sample.md
 * @see https://github.bamtech.co/fed-core/browser-sdk/blob/master/docs/reference/PlayerEvents.md
 * @see https://github.bamtech.co/fed-core/browser-sdk/blob/master/docs/reference/PlayerProperties.md
 * @see https://github.bamtech.co/bam-hls/bam-hls.js/blob/production-drm/API.md
 * @see https://github.bamtech.co/bam-hls/bam-hls.js/blob/production-drm/API.md#drm-configuration
 * @see https://github.bamtech.co/bam-hls/bam-hls.js/blob/production-drm/demo/index.html
 * @see https://github.bamtech.co/bam-hls/bam-hls.js/blob/development/API.md
 *
 */

import Check from 'check-types';
import Services from './../../services/entry';
import Drm from './../../drm/entry';
import PlayerAdapter from './../playerAdapter';
import PlaybackMetrics from './../playbackMetrics';
import PlaybackEventListener from './../playbackEventListener';

const Util = Services.Util;
const DrmProvider = Drm.DrmProvider;
const DrmType = Services.Media.DrmType;

const ErrorReason = Services.Exception.ErrorReason;
const ServiceException = Services.Exception.ServiceException;
const ExceptionReference = Services.Exception.ExceptionReference;
const ErrorCode = Services.Exception.ErrorCode;
const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    array,
    instanceStrict,
    nonEmptyString
} = Check.assert;

/**
 *
 * @class BamHlsPlayerAdapter
 * @extends {PlayerAdapter}
 * @desc Interface used to communicate with the media player.
 * @note BAM internal web player adapter
 *
 */
export default class BamHlsPlayerAdapter extends PlayerAdapter {

    /**
     *
     * @param {NativePlayer} nativePlayer An instance of the web-based bam-video-players
     * @throws {InvalidArgumentException}
     * @note The BamHlsPlayerAdapter requires nativePlayer.on && nativePlayer.off
     *
     */
    constructor(nativePlayer) {

        const errorMessage = {
            ctor: 'BamHlsPlayerAdapter(nativePlayer)',
            get nativePlayer() {
                return `${this.ctor} needs NativePlayer instance`;
            }
        };

        const nativePlayerInstance = (function nativePlayerInstance() {
            return Check.assigned(nativePlayer) && Check.assigned(nativePlayer.on);
        }());

        Check.assert(nativePlayerInstance, errorMessage.nativePlayer, InvalidArgumentException);

        super(nativePlayer);

        /**
         *
         * @access private
         * @type {Array<SDK.Drm.DrmProvider>}
         * @desc Set of DRM providers
         *
         */
        this.drmProviders = [];

        /**
         *
         * @access private
         * @type {HlsStream}
         *
         */
        this.hlsStatic = this.nativePlayer.getClass();

        /**
         *
         * @access private
         * @type {HlsStream.Events}
         * @note Events enum
         *
         */
        this.Events = this.hlsStatic.Events;

        /**
         *
         * @access private
         * @type {HlsStream.States}
         * @note States Enum
         *
         */
        this.States = this.hlsStatic.States;

        /**
         *
         * @access private
         * @type {HlsStream.DataTypes}
         * @note DataTypes Enum
         *
         */
        this.DataTypes = this.hlsStatic.DataTypes;

    }

    /**
     *
     * @access public
     * @param {String} playlistUri - The URL to be used during playback.
     * @desc Callback used when prepare has been called (usually via the {PlaybackSession}).
     * Sets the source URI on the {NativePlayer} instance.
     * @return {Promise<Void>}
     *
     */
    setSource(playlistUri) {

        const errorMessage = {
            method: 'BamHlsPlayerAdapter.setSource(playlistUri)',
            get playlistUri() {
                return `${this.method} playlistUri should be a String with length`;
            }
        };

        nonEmptyString(playlistUri, errorMessage.playlistUri, InvalidArgumentException);

        // only attempt to assign this if the user hasn't already done so when initializing bam-hls
        if (Check.not.assigned(this.nativePlayer.setupXhrCallback)) {
            this.setupXhrCallback();
        }

        return new Promise((resolve) => {

            this.playlistUri = playlistUri;

            return resolve();

        });

    }

    /**
     *
     * @access public
     * @desc Gets a snapshot of information about media playback.
     * @throws {ServiceException} Unable to get NativePlayer playhead or bitrate data
     * @returns {Promise<PlaybackMetrics>} - instance that contains a snapshot
     * of information about media playback.
     * @note metric value is rounded down to prevent possible service issues with floats
     * @note executed by {PlaybackTelemetryDispatcher#recordStreamSample}
     * @note (chosenBitRate / 1000) - need to convert to Kbps
     *
     */
    getPlaybackMetrics() {

        const errorMessage = {
            method: 'BamHlsPlayerAdapter.getPlaybackMetrics()',
            get currentPlayhead() {
                return `${this.method} unable to get NativePlayer playhead data`;
            },
            get currentBitrate() {
                return `${this.method} unable to get NativePlayer bitrate data`;
            }
        };

        const { nativePlayer, unavailableMetric } = this;

        if (Check.not.function(nativePlayer.getNetworkMetrics)) {

            const reasons = [new ErrorReason('', errorMessage.currentBitrate)];
            const exceptionData = ExceptionReference.common.invalidState;

            return Promise.reject(new ServiceException({ reasons, exceptionData }));

        }

        if (Check.not.number(nativePlayer.currentTime)) {

            const reasons = [new ErrorReason('', errorMessage.currentPlayhead)];
            const exceptionData = ExceptionReference.common.invalidState;

            return Promise.reject(new ServiceException({ reasons, exceptionData }));

        }

        const currentBitrate = Math.floor(nativePlayer.getNetworkMetrics().chosenBitrate / 1000) || unavailableMetric;
        const currentPlayhead = Math.floor(nativePlayer.currentTime) || unavailableMetric;

        return Promise.resolve(new PlaybackMetrics(currentBitrate, currentPlayhead));

    }

    /**
     *
     * @access private
     * @param {Object} exception - the exception object that bam-hls throws
     * @note exception.cause will be one value from the DRM section of this list:
     * https://github.bamtech.co/bam-hls/bam-hls.js/blob/master/src/hlsjs/errors.js
     * @desc Converts a bam-hls error to an error case object
     * @returns {Object} Associated error case for the given bam-hls error
     *
     */
    constructErrorCaseFromBamHlsError(exception) {

        const bamHlsError = exception.cause;

        let errorCode;

        switch (bamHlsError) {
            case 'drmLicenseRequestFailed':
                errorCode = ErrorCode.authorizationExpired;
                break;
            case 'drmLicenseExpired':
                errorCode = ErrorCode.invalidGrant;
                break;
            case 'drmHdcpError':
                errorCode = ErrorCode.mediaNotPlayable;
                break;
            case 'drmEmeMissing':
                errorCode = ErrorCode.mediaNotPlayable;
                break;
            case 'drmCdmInternalError':
                errorCode = ErrorCode.mediaNotPlayable;
                break;
            case 'drmNoSupportedKeySystem':
                errorCode = ErrorCode.mediaNotPlayable;
                break;
            case 'drmNoSupportedKeySystemConfiguration':
                errorCode = ErrorCode.unspecifiedDrmError;
                break;
            case 'drmEncryptionSchemeNotSupported':
                errorCode = ErrorCode.mediaNotPlayable;
                break;
            case 'drmVariantKeySystemMissing':
                errorCode = ErrorCode.keyNotFound;
                break;
            case 'drmMediakeysChangeFail':
                errorCode = ErrorCode.unspecifiedDrmError;
                break;
            case 'drmMultipleSessionsNotSupported':
                errorCode = ErrorCode.unspecifiedDrmError;
                break;
            case 'drmEmptyLicenseRequest':
                errorCode = ErrorCode.unspecifiedDrmError;
                break;
            default:
                errorCode = ErrorCode.unspecifiedDrmError;
        }

        const { code, exceptionData } = errorCode;
        const reasons = [new ErrorReason(code, null)];
        const errorCase = new ServiceException({ reasons, exceptionData });

        return errorCase;

    }

    /**
     *
     * @access public
     * @param {PlaybackEventListener} listener
     * @throws {ServiceException} Unable to add PlaybackEventListener
     * @returns {Promise<Void>}
     * @note messageDetailed is the error message pre-formatted with error, message and cause
     * from bam-hls STREAM_ERROR event
     *
     */
    addListener(listener) {

        const errorMessage = {
            method: 'BamHlsPlayerAdapter.addListener(listener)',
            get listener() {
                return `${this.method} needs PlaybackEventListener instance`;
            },
            get addListener() {
                return `${this.method} unable to add PlaybackEventListener`;
            }
        };

        instanceStrict(listener, PlaybackEventListener, errorMessage.listener, InvalidArgumentException);

        const { nativePlayer, boundHandlers, Events } = this;
        const { onPlay, onPause, onError, onPlayedToCompletion } = this;
        const { PLAYBACK_PLAYING, PLAYBACK_PAUSED, PLAYBACK_ENDED, STREAM_ERROR } = Events;

        return new Promise((resolve, reject) => {

            if (Check.assigned(nativePlayer.on)) {

                this.listener = listener;

                boundHandlers.onPlay = onPlay.bind(this);
                boundHandlers.onPause = onPause.bind(this);
                boundHandlers.onError = onError.bind(this);
                boundHandlers.onPlayedToCompletion = onPlayedToCompletion.bind(this);

                boundHandlers.onStreamError = (ex = {}) => {

                    const { fatal = false, messageDetailed = STREAM_ERROR } = ex;

                    if (fatal && ex.error === 'DRM_FAILED') {

                        const errorCase = this.constructErrorCaseFromBamHlsError(ex);

                        this.emit('MediaFailure', errorCase);

                        return boundHandlers.onError.call(this, errorCase);

                    } else if (fatal && Check.assigned(this.listener)) {

                        return boundHandlers.onError.call(this, messageDetailed);

                    } else {

                        return undefined;

                    }

                };

                nativePlayer.on(PLAYBACK_PLAYING, boundHandlers.onPlay);
                nativePlayer.on(PLAYBACK_PAUSED, boundHandlers.onPause);
                nativePlayer.on(PLAYBACK_ENDED, boundHandlers.onPlayedToCompletion);
                nativePlayer.on(STREAM_ERROR, boundHandlers.onStreamError);

                return resolve();

            }

            const reasons = [new ErrorReason('', errorMessage.addListener)];
            const exceptionData = ExceptionReference.common.invalidState;

            return reject(new ServiceException({ reasons, exceptionData }));

        });

    }

    /**
     *
     * @access public
     * @param {PlaybackEventListener} listener
     * @throws {ServiceException} Unable to remove PlaybackEventListener
     * @returns {Promise<Void>}
     *
     */
    removeListener(listener) {

        const errorMessage = {
            method: 'BamHlsPlayerAdapter.removeListener(listener)',
            get listener() {
                return `${this.method} needs PlaybackEventListener instance`;
            },
            get removeListener() {
                return `${this.method} unable to remove PlaybackEventListener`;
            }
        };

        instanceStrict(listener, PlaybackEventListener, errorMessage.listener, InvalidArgumentException);

        const { nativePlayer, boundHandlers, Events } = this;
        const { PLAYBACK_PLAYING, PLAYBACK_PAUSED, PLAYBACK_ENDED, STREAM_ERROR } = Events;

        if (Check.assigned(nativePlayer.off) && Check.equal(this.listener, listener)) {

            this.listener = null;

            nativePlayer.off(PLAYBACK_PLAYING, boundHandlers.onPlay);
            nativePlayer.off(PLAYBACK_PAUSED, boundHandlers.onPause);
            nativePlayer.off(PLAYBACK_ENDED, boundHandlers.onPlayedToCompletion);
            nativePlayer.off(STREAM_ERROR, boundHandlers.onStreamError);


        }

        return Promise.resolve();

    }

    /**
     *
     * @access public
     * @param {SDK.Drm.DrmProvider} drmProvider
     * @throws {ServiceException} This playerAdapter needs multiple SDK.Drm.DrmProvider instances, please
     * use BamHlsPlayerAdapter#setDrmProviders(drmProviders) which takes in an Array of SDK.Drm>DrmProvider instances
     *
     */
    setDrmProvider(drmProvider) { // eslint-disable-line

        const errorMessage = {
            method: 'BamHlsPlayerAdapter.setDrmProvider(drmProvider)',
            get multipleDrmProviders() {
                return `${this.method} - this playerAdapter needs multiple SDK.Drm.DrmProvider instances, please use BamHlsPlayerAdapter#setDrmProviders(drmProviders) which takes in an Array of SDK.Drm>DrmProvider instances`; // eslint-disable-line
            }
        };

        const reasons = [new ErrorReason('', errorMessage.multipleDrmProviders)];
        const exceptionData = ExceptionReference.common.invalidState;

        return Promise.reject(new ServiceException({ reasons, exceptionData }));

    }

    /**
     *
     * @access public
     * @param {Array<SDK.Drm.DrmProvider>} drmProviders
     * @note filters out unsupported drm keys before creating the drm configuration object
     * @returns {Promise<Void>}
     *
     */
    setDrmProviders(drmProviders) {

        const errorMessage = {
            method: 'BamHlsPlayerAdapter.setDrmProviders(drmProviders)',
            get drmProviders() {
                return `${this.method} needs Array of SDK.Drm.DrmProvider`;
            }
        };

        array.of.instanceStrict(drmProviders, DrmProvider, errorMessage.drmProviders, InvalidArgumentException);

        const { nativePlayer, setDrmConfiguration } = this;

        this.drmProviders = drmProviders;

        return nativePlayer.getDrmCapabilities().then((drmCapabilities) => {

            const { keySystems } = drmCapabilities;

            let drmProvidersIndex = drmProviders.length;

            while (drmProvidersIndex--) {

                if (!Util.getSafe(() => keySystems[drmProviders[drmProvidersIndex].type].supported)) { // eslint-disable-line
                    drmProviders.splice(drmProvidersIndex, 1);
                }

            }

            nativePlayer.drmConfiguration = setDrmConfiguration.bind(this)();

            return Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @desc Resets player adapter state. Removes all playback listeners.
     * @returns {Promise<Void>}
     *
     */
    clean() {

        const { listener, nativePlayer, boundHandlers, Events } = this;
        const { PLAYBACK_PLAYING, PLAYBACK_PAUSED, PLAYBACK_ENDED, STREAM_ERROR } = Events;

        if (Check.assigned(nativePlayer.off)) {

            nativePlayer.off(PLAYBACK_PLAYING, boundHandlers.onPlay);
            nativePlayer.off(PLAYBACK_PAUSED, boundHandlers.onPause);
            nativePlayer.off(PLAYBACK_ENDED, boundHandlers.onPlayedToCompletion);
            nativePlayer.off(STREAM_ERROR, boundHandlers.onStreamError);

        }

        this.playlistUri = '';
        this.boundHandlers = {};
        this.drmProvider = null;

        if (Check.assigned(listener)) {
            return this.removeListener(listener);
        }

        return Promise.resolve();

    }

    /* #region private: */

    /**
     *
     * @access private
     * @desc sets bam-hls.xhrSetupCallback to ensure a proper accessToken is passed on xhr requests
     * @note in the event of an AdEngine request we want to make `withCredentials=true` in case any platforms are
     * relying on cookies - not all platforms can support the `ssess` header
     *
     */
    setupXhrCallback() {

        const { nativePlayer, accessToken, DataTypes, adEngineData = {} } = this;
        const { ssess } = adEngineData;

        nativePlayer.xhrSetupCallback = (xhr, url, type) => {

            const isKeyCall = type === DataTypes.KEY;
            const isAdEngineCall = Util.adEngineRegex(url) && type === DataTypes.CHUNK;

            xhr.withCredentials = false; // Ensure no false positive from any cookies

            if (!xhr.readyState) {
                xhr.open('GET', url, true);
            }

            if (isKeyCall) {

                xhr.setRequestHeader('Authorization', accessToken);

            } else if (isAdEngineCall && Check.assigned(ssess)) {

                xhr.withCredentials = true;
                xhr.setRequestHeader('ssess', ssess);

            }

        };

    }

    /**
     *
     * @access private
     * @desc The DRM Configuration contains an array of key system configurations, one for
     * each supported key system. At a minimum, the key system configuration specifies
     * the key system, and a method to acquire a license, either a uri or a callback function.
     *
     * If a callback is provided, then the license request uri is optional, and would be available to the callback.
     * If no callback is provided, then the licenseRequestUri is not optional.
     *
     * @note licenseRequestHeaders expects a name/value set,
     * i.e. [{name: 'Content-Type', value: 'application/octet-stream'}]
     * @note serverCertifcate is mandatory for FAIRPLAY and recommended for WIDEVINE
     * @note serverCertificate is expected to be a Promise<BufferSource> or BufferSource
     * @see https://github.bamtech.co/bam-hls/bam-hls.js/blob/master/API.md#keysystemconfiguration
     * @returns {Object<Array>}
     *
     */
    setDrmConfiguration() {

        const { drmProviders } = this;
        const keySystems = [];

        for (const drmProvider of drmProviders) {

            const keySystem = drmProvider.type;
            const licenseRequestUri = Util.getSafe(() => drmProvider.licenseRequestUri);

            keySystems.push({
                keySystem,
                licenseRequestUri,
                licenseRequestHeadersCallback: () => drmProvider.formatRequestHeadersList(
                    drmProvider.processLicenseRequestHeaders()
                ),
                /**
                 *
                 * @note bam-hls calls `Promise.resolve(this._keySystemConfig.serverCertificate)`
                 * where serverCertificate is a reference to this function, in EME.js.
                 * We need to use an IIFE here so we setup a promise since they are not executing this function
                 * only expecting a Promise(cert), Promise(undefined), cert or undefined
                 *
                 */
                serverCertificate: (() => {

                    if (keySystem === DrmType.FAIRPLAY) {
                        return drmProvider.getFairPlayCertificate();
                    } else if (keySystem === DrmType.WIDEVINE) {
                        return drmProvider.getWidevineCertificate();
                    }

                    // bam-hls expects undefined so they don't attempt to process this as a certificate
                    return Promise.resolve(undefined);

                })()
            });

        }

        return {
            keySystems
        };

    }

    /**
     *
     * @access public
     * @desc Completes the cleanup process by completely cleaning up all `PlayerAdapter`
     * references. This method should be executed by the application developer
     * when they no longer need to use a `PlayerAdapter` instance.
     * @note Because `clean` is automatically called by the `PlaybackSession` instance,
     * another method is necessary to handle the use case of an application developer who
     * wants to continue using a `PlayerAdapter` instance after a `PlaybackSession` has been released.
     * @returns {Promise<Void>}
     *
     */
    dispose() {

        this.nativePlayer = null;
        this.accessToken = null;

        return Promise.resolve();

    }

}



// WEBPACK FOOTER //
// ./src/media/playerAdapter/bamHlsPlayerAdapter.js